/**
 * Pure geometry helpers for the FloatingWindow primitive: viewport reads,
 * clamping, cascade positioning, per-type size persistence, and z-index
 * derivation. Kept free of React so the store and tests can exercise them
 * directly (happy-dom has no layout engine, so all sizing math is arithmetic
 * on the values we hold, never `getBoundingClientRect`).
 */

import { safeExecute } from '@/utils/function.util';

import {
  BASE_POSITION,
  CASCADE_OFFSET,
  CASCADE_WRAP,
  DEFAULT_SIZE,
  MIN_SIZE,
  SIZE_STORAGE_PREFIX,
  Z_BASE,
  Z_MAX,
} from './floatingWindow.constants';

export interface Point {
  x: number;
  y: number;
}
export interface Size {
  w: number;
  h: number;
}

/** Current viewport in px, guarded for non-browser contexts. */
export function getViewport(): Size {
  if (typeof window === 'undefined') {
    return { w: DEFAULT_SIZE.w, h: DEFAULT_SIZE.h };
  }
  return {
    w: window.innerWidth || DEFAULT_SIZE.w,
    h: window.innerHeight || DEFAULT_SIZE.h,
  };
}

/** Clamp a size to [MIN_SIZE, viewport]. */
export function clampSize(size: Size, viewport: Size = getViewport()): Size {
  return {
    w: Math.max(MIN_SIZE.w, Math.min(size.w, viewport.w)),
    h: Math.max(MIN_SIZE.h, Math.min(size.h, viewport.h)),
  };
}

/**
 * Clamp a position so the window stays fully within the viewport when it fits
 * (never strands off-screen). If the window is larger than the viewport the
 * origin is pinned to 0 on that axis.
 */
export function clampPosition(
  pos: Point,
  size: Size,
  viewport: Size = getViewport()
): Point {
  return {
    x: Math.max(0, Math.min(pos.x, Math.max(0, viewport.w - size.w))),
    y: Math.max(0, Math.min(pos.y, Math.max(0, viewport.h - size.h))),
  };
}

/**
 * Cascade position for the Nth open instance of a type: BASE_POSITION nudged
 * down-right, wrapping after CASCADE_WRAP steps, then clamped to the viewport.
 */
export function cascadePosition(
  openCount: number,
  size: Size,
  viewport: Size = getViewport()
): Point {
  const step = openCount % CASCADE_WRAP;
  return clampPosition(
    {
      x: BASE_POSITION.x + step * CASCADE_OFFSET,
      y: BASE_POSITION.y + step * CASCADE_OFFSET,
    },
    size,
    viewport
  );
}

const sizeKey = (type: string) => `${SIZE_STORAGE_PREFIX}${type}`;

/**
 * Per-TYPE persisted size (not per-instance — concurrent runs of the same type
 * share one remembered size). Reads are guarded so blocked/full storage falls
 * back to the default instead of throwing during render.
 */
export function readPersistedSize(type: string): Size | null {
  const raw = safeExecute(() => localStorage.getItem(sizeKey(type)), null);
  if (!raw) return null;
  const parsed = safeExecute<Size | null>(() => JSON.parse(raw), null);
  if (
    parsed &&
    typeof parsed.w === 'number' &&
    typeof parsed.h === 'number' &&
    parsed.w >= MIN_SIZE.w &&
    parsed.h >= MIN_SIZE.h
  ) {
    return parsed;
  }
  return null;
}

export function writePersistedSize(type: string, size: Size): void {
  safeExecute(
    () => localStorage.setItem(sizeKey(type), JSON.stringify(size)),
    undefined
  );
}

/**
 * Derive the CSS z-index for a window from its rank in `focusOrder` (last =
 * most recently focused = highest). Bounded to [Z_BASE, Z_MAX]; when there are
 * more windows than the band width, ranks compress so the top window still
 * wins and nothing exceeds Z_MAX (keeping in-body dropdowns at z-200 on top).
 */
export function computeZIndex(focusOrder: string[], id: string): number {
  const rank = focusOrder.indexOf(id);
  if (rank < 0) return Z_BASE;
  const span = Z_MAX - Z_BASE;
  const count = focusOrder.length;
  if (count <= 1) return Z_MAX;
  // Map rank 0..count-1 onto Z_BASE..Z_MAX, compressing when count-1 > span.
  const offset = Math.round((rank / (count - 1)) * span);
  return Z_BASE + Math.min(offset, span);
}
